import { floorToBarPeriod } from '../timezone.js'

function finiteNumber(value) {
  if (value == null || typeof value === 'boolean' || (typeof value === 'string' && value.trim() === '')) return null
  const n = Number(value)
  return Number.isFinite(n) ? n : null
}

function finitePositivePrice(value) {
  const n = finiteNumber(value)
  return n != null && n > 0 ? n : null
}

function executionTimeSeconds(execution, { timeframe = 'daily', timezone } = {}) {
  const barTimeRef = finiteNumber(execution?.bar_time_ref)
  if (barTimeRef != null && barTimeRef > 0) return barTimeRef

  const ms = new Date(execution?.executed_at || execution?.timestamp || 0).getTime()
  if (!Number.isFinite(ms) || ms <= 0) return null

  if (timeframe === 'daily' || timeframe === 'weekly') {
    // Daily/weekly candle identities are exchange dates, independent of the
    // user's display timezone (and of the UTC date after the US close).
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit',
    }).formatToParts(new Date(ms))
    const part = (type) => Number(parts.find(value => value.type === type)?.value)
    const date = new Date(Date.UTC(part('year'), part('month') - 1, part('day')))
    if (timeframe === 'weekly') date.setUTCDate(date.getUTCDate() - (date.getUTCDay() + 6) % 7)
    return date.getTime() / 1000
  }

  return floorToBarPeriod(new Date(ms), timeframe, timezone) || null
}

function executionId(execution, index) {
  if (execution?.id) return `execution:${execution.id}`
  return [
    'execution',
    execution?.trade_id || 'unknown-trade',
    execution?.executed_at || execution?.timestamp || 'unknown-time',
    execution?.side || 'unknown-side',
    execution?.price ?? 'unknown-price',
    execution?.quantity ?? 'unknown-qty',
    index,
  ].join(':')
}

function executionType(execution) {
  const rawType = `${execution?.type || ''} ${execution?.execution_type || ''}`.toLowerCase()
  if (rawType === 'stop' || rawType === 'stop-hit' || rawType.includes('stop')) return 'stop-hit'
  if (rawType === 'target' || rawType === 'target-hit' || rawType.includes('target')) return 'target-hit'
  return 'execution'
}

function executionSide(execution) {
  const side = String(execution?.side || '').toLowerCase()
  if (side === 'buy' || side === 'buy_to_open' || side === 'buy_to_close') return 'buy'
  if (side === 'sell' || side === 'sell_to_open' || side === 'sell_to_close') return 'sell'
  return 'neutral'
}

function markerIconKey(type, side) {
  if (type === 'stop-hit') return 'shield'
  if (side === 'buy') return 'arrow-up'
  if (side === 'sell') return 'arrow-down'
  return 'flag'
}

function formatQty(value) {
  const qty = finiteNumber(value)
  if (qty == null) return null
  return Number.isInteger(qty) ? String(qty) : String(Number(qty.toFixed(4)))
}

function buildExecutionTooltip(execution, side, price, qty) {
  const rows = [
    { left: 'Side', right: side === 'neutral' ? null : side.toUpperCase() },
    { left: 'Price', right: price == null ? null : String(price) },
    { left: 'Qty', right: qty },
  ]
  if (execution?.account_number || execution?.account) {
    rows.push({ left: 'Account', right: String(execution.account_number || execution.account).slice(-4) })
  }
  if (execution?.execution_type) {
    rows.push({ left: 'Type', right: String(execution.execution_type).replace(/_/g, ' ') })
  }
  if (execution?._consolidated && execution?._count) {
    rows.push({ left: 'Grouped', right: String(execution._count) })
  }
  return {
    headline: side === 'neutral' ? 'Execution' : `${side.toUpperCase()} execution`,
    rows,
  }
}

/**
 * Executions on the same candle with the same side/type inside a 5-minute
 * window and within 1% price drift render as one
 * marker with child rows for the hover tooltip.
 */
export function consolidateExecutions(executions, options = {}) {
  if (!executions || executions.length === 0) return []

  const WINDOW_MS = 5 * 60 * 1000
  const PRICE_THRESHOLD = 0.01

  const sorted = [...executions].filter(e => e.executed_at && Number.isFinite(Date.parse(e.executed_at))).sort(
    (a, b) => new Date(a.executed_at) - new Date(b.executed_at)
  )

  const groups = []
  let currentGroup = null

  for (const exec of sorted) {
    const time = new Date(exec.executed_at).getTime()
    const price = Number(exec.price) || 0
    const side = exec.side || 'buy'
    const execType = exec.execution_type || ''
    const candleTime = executionTimeSeconds(exec, options)

    if (
      currentGroup &&
      currentGroup.side === side &&
      currentGroup.execType === execType &&
      candleTime != null && currentGroup.candleTime === candleTime &&
      time - currentGroup.earliestTime <= WINDOW_MS &&
      price > 0 && currentGroup.avgPrice > 0 &&
      Math.abs(price - currentGroup.avgPrice) / currentGroup.avgPrice <= PRICE_THRESHOLD
    ) {
      currentGroup.items.push(exec)
      currentGroup.totalPrice += price
      currentGroup.totalQty += Number(exec.quantity) || 0
      currentGroup.avgPrice = currentGroup.totalPrice / currentGroup.items.length
    } else {
      currentGroup = {
        side,
        execType,
        earliestTime: time,
        candleTime,
        items: [exec],
        totalPrice: price,
        totalQty: Number(exec.quantity) || 0,
        avgPrice: price,
        firstExec: exec,
      }
      groups.push(currentGroup)
    }
  }

  return groups.map(g => ({
    ...g.firstExec,
    quantity: g.totalQty,
    price: g.avgPrice,
    _count: g.items.length,
    _consolidated: g.items.length > 1,
    _children: g.items,
    _totalQty: g.totalQty,
    _avgPrice: g.avgPrice,
  }))
}

function executionChildren(execution) {
  const rows = Array.isArray(execution?._children) && execution._children.length > 0
    ? execution._children
    : [execution]
  return rows.map((child) => ({
    account_number: child?.account_number || child?.account || null,
    quantity: finiteNumber(child?.quantity) ?? undefined,
    price: finitePositivePrice(child?.price) ?? finitePositivePrice(child?._avgPrice) ?? undefined,
    executed_at: child?.executed_at || child?.timestamp || null,
    side: child?.side,
    execution_type: child?.execution_type,
  }))
}

export function buildExecutionMarkers(executions = [], options = {}) {
  return (Array.isArray(executions) ? executions : [])
    .map((execution, index) => {
      const time = executionTimeSeconds(execution, options)
      const price = finitePositivePrice(execution?.price) ?? finitePositivePrice(execution?._avgPrice)
      if (time == null || price == null) return null

      const type = executionType(execution)
      const side = executionSide(execution)
      const qty = formatQty(execution?._totalQty ?? execution?.quantity)
      return {
        id: executionId(execution, index),
        price,
        time,
        ...(finiteNumber(execution?.bar_time_ref) > 0 ? { bar_time_ref: time } : {}),
        type,
        state: 'rest',
        side,
        iconKey: markerIconKey(type, side),
        label: qty ? `${side === 'neutral' ? 'EXEC' : side.toUpperCase()} ${qty}` : undefined,
        tooltip: buildExecutionTooltip(execution, side, price, qty),
        metadata: {
          trade_id: execution?.trade_id,
          account: execution?.account_number || execution?.account,
          qty: finiteNumber(execution?._totalQty ?? execution?.quantity) ?? undefined,
          executed_at: execution?.executed_at || execution?.timestamp || undefined,
          execution_type: execution?.execution_type,
          consolidated_count: execution?._count,
          children: executionChildren(execution),
        },
      }
    })
    .filter(Boolean)
}

function visibleLine(price, extras = null) {
  return extras ? { price, visible: true, ...extras } : { price, visible: true }
}

function normalizeLegacySetupLines(setupLines) {
  if (!setupLines) return null
  const next = {}
  const triggerPrice = finitePositivePrice(setupLines.triggerPrice)
  const stopPrice = finitePositivePrice(setupLines.patternFailurePrice)
  const targetPrice = finitePositivePrice(setupLines.sellTargetPrice)
  if (triggerPrice != null) next.entry = visibleLine(triggerPrice)
  if (stopPrice != null) next.stop = visibleLine(stopPrice)
  if (targetPrice != null) next.target = visibleLine(targetPrice)
  return Object.keys(next).length > 0 ? next : null
}

function normalizeStopLines(stopLines, stopLoss) {
  const normalized = (Array.isArray(stopLines) ? stopLines : [])
    .map((line, index) => {
      const price = finitePositivePrice(line?.price)
      if (price == null) return null
      const accounts = Array.isArray(line?.accounts) ? line.accounts.filter(Boolean) : []
      return {
        trancheId: line?.trancheId || line?.tranche_id || `stop-${index + 1}`,
        price,
        label: 'Stop',
        accounts,
        totalQty: finiteNumber(line?.totalQty) ?? undefined,
      }
    })
    .filter(Boolean)

  if (normalized.length > 0) return normalized
  const fallback = finitePositivePrice(stopLoss)
  return fallback == null
    ? []
    : [{ trancheId: 'stop-1', price: fallback, label: 'Stop', accounts: [] }]
}

function activeStopPriceSet(activeStopPrices) {
  return new Set(
    (Array.isArray(activeStopPrices) ? activeStopPrices : [])
      .map(finiteNumber)
      .filter((price) => price != null)
      .map((price) => price.toFixed(4))
  )
}

function stopIsActive(price, activeStops) {
  const n = finiteNumber(price)
  return n != null && activeStops.has(n.toFixed(4))
}

function mergeSetupAndStops(legacySetupLines, stopDescriptors, activeStopPrices) {
  const next = legacySetupLines ? { ...legacySetupLines } : {}
  const activeStops = activeStopPriceSet(activeStopPrices)
  if (stopDescriptors.length === 1) {
    next.stop = visibleLine(stopDescriptors[0].price, { active: stopIsActive(stopDescriptors[0].price, activeStops) })
  } else if (stopDescriptors.length > 1) {
    delete next.stop
    next.trancheStops = stopDescriptors.map(({ trancheId, price, label }) => ({
      trancheId,
      price,
      label,
      active: stopIsActive(price, activeStops),
    }))
  }
  return Object.keys(next).length > 0 ? next : null
}

function buildPositionLines({ entryPrice, assetType, stage }) {
  const price = finitePositivePrice(entryPrice)
  if (price == null) return null
  if (assetType === 'option') return null
  if (stage && stage !== 'active') return null
  return { avgCost: { price } }
}

function normalizeActiveStops(activeStopPrice) {
  return (Array.isArray(activeStopPrice) ? activeStopPrice : [activeStopPrice])
    .map(finitePositivePrice)
    .filter((price) => price != null)
}

export function buildPositionOverlayProps({
  executions = [],
  setupLines = null,
  stopLines = null,
  stopLoss = null,
  entryPrice = null,
  assetType = 'equity',
  stage = null,
  activeStopPrice = null,
  timeframe = 'daily',
  timezone = null,
} = {}) {
  const stopDescriptors = normalizeStopLines(stopLines, stopLoss)
  const activeStopPrices = normalizeActiveStops(activeStopPrice)
  return {
    markers: buildExecutionMarkers(executions, { timeframe, timezone }),
    setupLines: mergeSetupAndStops(normalizeLegacySetupLines(setupLines), stopDescriptors, activeStopPrices),
    positionLines: buildPositionLines({ entryPrice, assetType, stage }),
    overlayState: {
      stopLines: stopDescriptors,
      activeStopPrices,
    },
  }
}
